import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsUpDownLeftRight } from '@fortawesome/free-solid-svg-icons';
import AnnotatableImage from './AnnotatableImage';

// Keep the floating panel below the sticky app header (and the Evaluate
// sub-tab strip when present) so it cannot tuck under the menu bar.
function getDragMinY() {
  let minY = 0;
  if (typeof document === 'undefined') return 56;
  const header = document.querySelector('.app-header');
  if (header) minY = Math.max(minY, header.getBoundingClientRect().bottom);
  const subnav = document.querySelector('[data-evaluate-subnav]');
  if (subnav) minY = Math.max(minY, subnav.getBoundingClientRect().bottom);
  if (minY <= 0) {
    const cssH = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    minY = (Number.isFinite(cssH) ? cssH : 52);
  }
  return Math.ceil(minY) + 4;
}

// Floating, draggable single-image preview. NON-MODAL by design: there is
// no full-screen backdrop, so the table behind the panel stays fully
// interactive — the grader can edit rows while comparing them to the scan.
// Opens anchored to the RIGHT (default) or LEFT side of the screen; grab
// the dark title bar to drag it anywhere. The image area keeps its own
// scroll-zoom / drag-pan (AnnotatableImage), so window-move and image-pan
// never fight.
//
// Props:
//   url:              string
//   name:             string
//   onClose:          () => void
//   width:            string        // CSS width of the panel (default '50vw')
//   imageHeight:      string        // CSS height of the image area (default '80vh')
//   anchor:           'left'|'right'// which side to open on (default 'right')
//   stickTop:         boolean       // open flush with the top of the window
//   initialFocus:     string|null   // forwarded to AnnotatableImage ('top20', etc.)
//   initialZoom:      number|null   // e.g. 4.76 for 476%
//   hugPaper:         boolean       // shrink the panel to the zoomed page
//   defaultTool:      string
//   availableTools:   string[]|null
//   fixedStrokeWidth: number|null
//
// Keyboard: Esc closes — but only when focus isn't inside an
// input/textarea/select/contentEditable, so typing into the table is
// never hijacked.
export default function DraggableImagePanel({
  url,
  name,
  onClose,
  width = '50vw',
  imageHeight = '80vh',
  anchor = 'right',
  stickTop = false,
  initialFocus = null,
  initialZoom = null,
  hugPaper = false,
  defaultTool,
  availableTools,
  fixedStrokeWidth
}) {
  // Default position: right (or left) side of the screen, just below the
  // header. stickTop opens flush with the top edge of the window instead.
  const [pos, setPos] = useState(() => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const vw = parseFloat(width) || 50;
    const panelPx = (w * vw) / 100;
    const x = anchor === 'left'
      ? 12
      : Math.max(0, Math.round(w - panelPx - (stickTop ? 0 : 16)));
    return { x, y: stickTop ? 0 : getDragMinY() };
  });
  const [dragging, setDragging] = useState(false);
  const [fittedPx, setFittedPx] = useState(null);
  const dragRef = useRef(null); // { sx, sy, ox, oy } captured on drag start

  const cssPx = (value) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return 0;
    const text = String(value);
    if (text.endsWith('vh')) return window.innerHeight * n / 100;
    if (text.endsWith('px')) return n;
    return window.innerWidth * n / 100;
  };

  // The zoomed page is narrower than a wide panel, which leaves empty
  // space on both sides of the paper. Size the window to that page.
  useEffect(() => {
    if (!hugPaper || typeof initialZoom !== 'number' || !url) {
      setFittedPx(null);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled || !img.naturalWidth || !img.naturalHeight) return;
      const heightPx = cssPx(imageHeight);
      const aspect = img.naturalWidth / img.naturalHeight;
      if (!heightPx || !aspect) return;
      const toolbarAndGap = 48 + 8;
      const paperWidth = Math.ceil(heightPx * aspect * initialZoom + toolbarAndGap);
      const cap = Math.min(window.innerWidth * 0.96, cssPx(width) || paperWidth);
      const next = Math.max(280, Math.min(paperWidth, cap));
      setFittedPx(next);
      setPos((current) => ({
        ...current,
        x: anchor === 'left'
          ? 12
          : Math.max(0, Math.round(window.innerWidth - next)),
        y: stickTop ? 0 : current.y
      }));
    };
    img.src = url;
    return () => { cancelled = true; };
  }, [hugPaper, url, initialZoom, imageHeight, width, stickTop, anchor]);

  // Esc closes, but never while the user is editing a form field.
  useEffect(() => {
    const isEditable = (el) =>
      !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
    const onKey = (e) => {
      if (e.key === 'Escape' && !isEditable(e.target)) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Window-drag: grab the title bar to record the offset, then global
  // mousemove moves the panel until mouseup. A soft clamp keeps the panel
  // from being lost off-screen / under the menu bar.
  const onDragStart = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
    setDragging(true);
  };
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      let nx = d.ox + (e.clientX - d.sx);
      let ny = d.oy + (e.clientY - d.sy);
      const panelPx = fittedPx || cssPx(width);
      const minX = -panelPx + 120; // keep title bar partly visible
      const maxX = window.innerWidth - 60;
      const minY = stickTop ? 0 : getDragMinY();
      const maxY = window.innerHeight - 40;
      nx = Math.min(maxX, Math.max(minX, nx));
      ny = Math.min(maxY, Math.max(minY, ny));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => { dragRef.current = null; setDragging(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, width, fittedPx, stickTop]);

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: fittedPx ? `${fittedPx}px` : width,
        maxWidth: '96vw',
        zIndex: 9999,
        // No backdrop on purpose — the page (and the table) stays
        // interactive. Pointer events only land on this floating panel.
        filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.45))'
      }}
    >
      {/* Drag handle / title bar — grab here to move the whole panel. */}
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          background: 'rgba(17,24,39,0.92)',
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
      >
        <FontAwesomeIcon icon={faArrowsUpDownLeftRight} style={{ opacity: 0.85, fontSize: 12 }} />
        <span style={{
          flex: 1,
          textAlign: 'center',
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {name}
        </span>
        <button
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          title="Close (Esc)"
          style={{
            background: '#EF4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: 24,
            height: 24,
            lineHeight: '24px',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 15,
            flexShrink: 0
          }}
        >×</button>
      </div>

      {/* Image area. */}
      <div style={{
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        overflow: 'hidden'
      }}>
        <AnnotatableImage
          url={url}
          alt={name || 'scan'}
          width="100%"
          height={imageHeight}
          background="transparent"
          initialFocus={initialFocus}
          initialZoom={initialZoom}
          defaultTool={defaultTool}
          availableTools={availableTools}
          fixedStrokeWidth={fixedStrokeWidth}
        />
      </div>
    </div>
  );
}
